import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { ScenarioRunStatusPopup } from '@/components/eval-studio/ScenarioRunStatusPopup';
import { useEvalStudioScenarioRunRetrieve } from '@/components/eval-studio/evalStudioApi';
import {
  readTrackedScenarioRuns,
  writeTrackedScenarioRuns,
  type PendingScenarioRunMeta,
  type TrackedScenarioRunMeta,
} from '@/components/eval-studio/scenarioRunTrackingStorage';
import { useAuthStore } from '@/store/authStore';

interface ScenarioRunTrackingContextValue {
  startPendingRun: (meta: Omit<PendingScenarioRunMeta, 'startedAt'>) => void;
  trackScenarioRun: (meta: Omit<TrackedScenarioRunMeta, 'startedAt'>) => void;
  clearPendingRun: () => void;
  pendingRun: PendingScenarioRunMeta | null;
}

const ScenarioRunTrackingContext = createContext<ScenarioRunTrackingContextValue | null>(null);

function TrackedScenarioRunPopup({
  meta,
  onDismiss,
}: {
  meta: TrackedScenarioRunMeta;
  onDismiss: (runId: string) => void;
}) {
  const runQuery = useEvalStudioScenarioRunRetrieve(meta.runId);
  const run = runQuery.data;
  const pending = runQuery.isLoading && !run;

  useEffect(() => {
    if (runQuery.isError) {
      onDismiss(meta.runId);
    }
  }, [meta.runId, onDismiss, runQuery.isError]);

  return (
    <ScenarioRunStatusPopup
      run={run}
      pending={pending}
      scenarioName={meta.scenarioName}
      versionLabel={meta.versionLabel}
      isDraft={meta.isDraft}
      onDismiss={() => onDismiss(meta.runId)}
      embedded
    />
  );
}

export function ScenarioRunTrackingProvider({ children }: { children: React.ReactNode }) {
  const { currentWorkspace } = useAuthStore();
  const workspaceId = currentWorkspace?.id ?? null;
  const queryClient = useQueryClient();
  const [trackedRuns, setTrackedRuns] = useState<TrackedScenarioRunMeta[]>([]);
  const [pendingRun, setPendingRun] = useState<PendingScenarioRunMeta | null>(null);

  useEffect(() => {
    if (!workspaceId) {
      setTrackedRuns([]);
      return;
    }
    setTrackedRuns(readTrackedScenarioRuns(workspaceId));
  }, [workspaceId]);

  const startPendingRun = useCallback((meta: Omit<PendingScenarioRunMeta, 'startedAt'>) => {
    setPendingRun({ ...meta, startedAt: new Date().toISOString() });
  }, []);

  const clearPendingRun = useCallback(() => {
    setPendingRun(null);
  }, []);

  const trackScenarioRun = useCallback(
    (meta: Omit<TrackedScenarioRunMeta, 'startedAt'>) => {
      setPendingRun(null);
      queryClient.invalidateQueries({ queryKey: ['eval-studio', 'scenario-runs', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['eval-studio', 'scenarios', 'with-single-runs'] });
      setTrackedRuns((previous) => {
        const next = [
          { ...meta, startedAt: new Date().toISOString() },
          ...previous.filter((run) => run.runId !== meta.runId),
        ];
        if (workspaceId) {
          writeTrackedScenarioRuns(workspaceId, next);
        }
        return next;
      });
    },
    [queryClient, workspaceId]
  );

  const dismissRun = useCallback(
    (runId: string) => {
      setTrackedRuns((previous) => {
        const next = previous.filter((run) => run.runId !== runId);
        if (workspaceId) {
          writeTrackedScenarioRuns(workspaceId, next);
        }
        return next;
      });
    },
    [workspaceId]
  );

  const contextValue = useMemo<ScenarioRunTrackingContextValue>(
    () => ({
      startPendingRun,
      trackScenarioRun,
      clearPendingRun,
      pendingRun,
    }),
    [clearPendingRun, pendingRun, startPendingRun, trackScenarioRun]
  );

  const hasPopups = Boolean(pendingRun) || trackedRuns.length > 0;

  return (
    <ScenarioRunTrackingContext.Provider value={contextValue}>
      {children}
      {hasPopups ? (
        <div
          data-testid="scenario-run-tracking-popups"
          className="fixed bottom-4 right-4 z-[110] flex w-[360px] max-w-[calc(100vw-2rem)] flex-col gap-2"
        >
          {pendingRun ? (
            <ScenarioRunStatusPopup
              pending
              scenarioName={pendingRun.scenarioName}
              versionLabel={pendingRun.versionLabel}
              isDraft={pendingRun.isDraft}
              onDismiss={() => undefined}
              embedded
            />
          ) : null}
          {trackedRuns.map((meta) => (
            <TrackedScenarioRunPopup key={meta.runId} meta={meta} onDismiss={dismissRun} />
          ))}
        </div>
      ) : null}
    </ScenarioRunTrackingContext.Provider>
  );
}

export function useScenarioRunTracking(): ScenarioRunTrackingContextValue {
  const context = useContext(ScenarioRunTrackingContext);
  if (!context) {
    throw new Error('useScenarioRunTracking must be used within ScenarioRunTrackingProvider');
  }
  return context;
}
